import { EmailIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Box, Flex,
    FormControl,
    FormLabel, IconButton, Input,
    SimpleGrid, VisuallyHidden
} from '@chakra-ui/react';
import { useShoppingCart } from 'context/shoppingCartContext';
import { useRouter } from 'next/router';
import { signIn } from 'next-auth/react';
import { FunctionComponent, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useHookForm } from '@/hooks/useHookForm';
import { useValidation } from '@/hooks/useValidation';

import { FormControlError } from '@/components/FormControlError/FormControlError';
import { Button } from '@/components/UI/Buttons/Button';
import MemberNumberOrEmailInput from '@/components/UI/Inputs/MemberNumberOrEmailInput';
import { PasswordInput } from '@/components/UI/Inputs/Password/PasswordInput';
import NavLink from '@/components/UI/Links/NavLink';

import RoutePath from '@/constants/route-path';
import { LoginForm as LoginFormModel } from '@/models/forms/LoginForm';
import { AppService } from '@/services';
import { EnvironmentService } from '@/services/EnvironmentService';
import { emailValidation } from '@/validators/email';
import { representativeNoValidation } from '@/validators/representativeNo';
import {BasketIcon} from "@/components/Icons";
import ProductQuantityButton from "@/components/ShoppingCart/ProductQuantityButton";

type LoginFormProps = {
    fromBasket?: boolean;
    variant?: 'default' | 'power-panel';
    darkBackground?: boolean;
};

export const LoginForm: FunctionComponent<LoginFormProps> = ({
    fromBasket = false,
    variant = 'default',
    darkBackground
}) => {
    const isPowerPanel = variant === 'power-panel';
    const isDarkBackground = darkBackground ?? isPowerPanel;
    const router = useRouter();
    const intl = useIntl();
    const instanceCountryCode = useMemo(() => AppService.getAppCountryCode(), []);
    const { formState, handleSubmit, register, setValue, resetField } =
        useHookForm<LoginFormModel>();
    const passwordPlaceholderText = intl.formatMessage({ id: 'enter-password' });
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [isAccountLockedError, setIsAccountLockedError] = useState(false);
    const [isAccountDisabledError, setIsAccountDisabledError] = useState(false);
    const contactEmail = AppService.getContactEmailAddress();
    const { getValidationKey } = useValidation();
    const { cartItemsUnlogged } = useShoppingCart();

    const usernameFormControl = register('memberNumber', {
        required: {
            value: true,
            message: 'field-is-required'
        },
        validate: (value: string) => {
            if (value.includes('@')) {
                return emailValidation(value);
            }
            return representativeNoValidation(value);
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            const normalized = normalizeMemberNumber(e.target.value);
            setValue('memberNumber', normalized, { shouldValidate: true });
        }
    });
    const passwordFormControl = register('password', {
        required: {
            value: true,
            message: 'field-is-required'
        }
    });

    const normalizeMemberNumber = (value: string): string => {
        if (value.includes('@')) {
            return value;
        }
        if (/^\d+$/.test(value)) {
            return `${instanceCountryCode}-${value}`;
        }
        return value.toUpperCase();
    };

    const loginFormSubmit: SubmitHandler<LoginFormModel> = async (controls) => {
        const redirectURL = async () => {
            const redirectURL = fromBasket
                ? RoutePath.Checkout
                : (router.query.redirectURL as string | undefined);

            if (cartItemsUnlogged.length > 0) {
                if (redirectURL) {
                    return router.replace(redirectURL);
                }

                return router.replace(RoutePath.Products);
            }

            if (redirectURL) {
                const filesPathPosition = redirectURL.indexOf(RoutePath.Files);
                const filename = redirectURL.substring(filesPathPosition);

                return router.push(RoutePath.Files, {
                    pathname: filename
                });
            }

            const redirectPath = router.query.redirect as string | undefined;

            if (!redirectPath) {
                return router.push(RoutePath.Products);
            }

            if (redirectPath === RoutePath.SignOut) {
                return (location.href = EnvironmentService.getCanonicalURL());
            }

            return router.push(decodeURIComponent(redirectPath));
        };

        controls.memberNumber = controls.memberNumber.trim();
        if (
            !controls.memberNumber.includes('@') &&
            !controls.memberNumber.startsWith(instanceCountryCode)
        ) {
            controls.memberNumber = `${instanceCountryCode}-${controls.memberNumber}`;
        }
        setFormError('');
        setSubmitting(true);
        signIn('credentials', {
            ...controls,
            redirect: false
        })
            .then(async (response) => {
                setSubmitting(false);
                if (!response) {
                    return;
                }
                const { ok, error } = response;

                if (error) {
                    setIsAccountLockedError(() => error === 'account.locked');
                    setIsAccountDisabledError(() => error === 'account.disabled');
                    setFormError(() => error);
                    resetField('password');
                    return;
                }
                if (!ok) {
                    return;
                }

                sessionStorage.removeItem('sponsorNo');

                await redirectURL();
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <form noValidate onSubmit={handleSubmit(loginFormSubmit)}>
            <Box>
                {formError && (
                    <Alert status="error" marginBottom={4} whiteSpace="pre-wrap">
                        <AlertIcon />
                        <AlertDescription>
                            <FormattedMessage id={getValidationKey(formError)} />
                            {isAccountDisabledError && (
                                <>
                                    {' '}
                                    <NavLink href={`mailto:${contactEmail}`} isExternal>
                                        {contactEmail}
                                    </NavLink>
                                    .
                                </>
                            )}
                            {isAccountLockedError && (
                                <Box>
                                    <EmailIcon fontSize="20px" mr={1} />
                                    <NavLink href={`mailto:${contactEmail}`} isExternal>
                                        {contactEmail}
                                    </NavLink>
                                </Box>
                            )}
                        </AlertDescription>
                    </Alert>
                )}
                <SimpleGrid
                    as="fieldset"
                    columns={{
                        base: 1,
                        sm: fromBasket ? 1 : 2
                    }}
                    gap={fromBasket ? 6 : 4}
                >
                    <FormControl
                        isInvalid={!!formState.errors[usernameFormControl.name]}
                        isRequired
                        marginBottom={0}
                    >
                        <FormLabel color={isDarkBackground ? 'white' : undefined}>
                            <FormattedMessage id="member-number-or-email" />
                        </FormLabel>
                        <FormControlError control={usernameFormControl} formState={formState}>
                            <MemberNumberOrEmailInput formControlRef={usernameFormControl} />
                        </FormControlError>
                    </FormControl>
                    <FormControl
                        isInvalid={!!formState.errors[passwordFormControl.name]}
                        isRequired
                        marginBottom={0}
                    >
                        <FormLabel color={isDarkBackground ? 'white' : undefined}>
                            <FormattedMessage id="password" />
                        </FormLabel>
                        <FormControlError control={passwordFormControl} formState={formState}>
                            <PasswordInput
                                inputProps={{
                                    autoComplete: 'current-password',
                                    placeholder: passwordPlaceholderText,
                                    ...passwordFormControl
                                }}
                            />
                        </FormControlError>
                    </FormControl>
                </SimpleGrid>
                <FormControl textAlign="right">
                    <NavLink
                        href={RoutePath.RemindPassword}
                        fontWeight="medium"
                        color={isDarkBackground ? 'white' : 'black'}
                        fontSize="sm"
                    >
                        <FormattedMessage id="forgot-password"></FormattedMessage>
                    </NavLink>
                </FormControl>
                <Box textAlign="center">
                    <FormControl mb="2.5" mt={fromBasket ? 8 : 0}>
                        <Button
                            type="submit"
                            colorScheme="brand"
                            isLoading={submitting}
                            w={fromBasket ? 'full' : 'auto'}
                        >
                            <FormattedMessage id="log-in"></FormattedMessage>
                        </Button>
                    </FormControl>
                </Box>
                {!isPowerPanel && (
                    <Box textAlign="center">
                        <Button
                            as="a"
                            href={RoutePath.Contact}
                            variant="outline"
                            h={12}
                            mt={10}
                            colorScheme="brand"
                        >
                            <FormattedMessage id="become-our-partner"></FormattedMessage>
                        </Button>
                    </Box>
                )}
            </Box>
        </form>
    );
};
